import { defineMessages } from 'react-intl-next';

import type { SemanticColor } from '@atlaskit/lozenge';

const pullRequestStatusToLozengeMap: Record<string, SemanticColor> = {
	OPEN: 'information', // blue
	MERGED: 'success', // green
	DECLINED: 'danger', // red
	DRAFT: 'neutral', // grey
};

export const getPullRequestStatusAppearance = (status: string): SemanticColor =>
	pullRequestStatusToLozengeMap[status] ?? 'neutral';

export const pullRequestStatusMessages = defineMessages({
	status_open: {
		id: 'search-pull-request-result.status.open',
		defaultMessage: 'Open',
		description: 'Pull request status label for open pull requests',
	},
	status_merged: {
		id: 'search-pull-request-result.status.merged',
		defaultMessage: 'Merged',
		description: 'Pull request status label for merged pull requests',
	},
	status_declined: {
		id: 'search-pull-request-result.status.declined',
		defaultMessage: 'Declined',
		description: 'Pull request status label for declined pull requests',
	},
	status_draft: {
		id: 'search-pull-request-result.status.draft',
		defaultMessage: 'Draft',
		description: 'Pull request status label for draft pull requests',
	},
	status_unknown: {
		id: 'search-pull-request-result.status.unknown',
		defaultMessage: 'Unknown',
		description: 'Pull request status label for pull requests with unknown status',
	},
});

/** Maps pull request status values to i18n message descriptors */
export const pullRequestStatusToMessage: Record<
	string,
	(typeof pullRequestStatusMessages)[keyof typeof pullRequestStatusMessages]
> = {
	OPEN: pullRequestStatusMessages.status_open,
	MERGED: pullRequestStatusMessages.status_merged,
	DECLINED: pullRequestStatusMessages.status_declined,
	DRAFT: pullRequestStatusMessages.status_draft,
	UNKNOWN: pullRequestStatusMessages.status_unknown,
};

/** Returns the i18n message descriptor for a pull request status, falling back to unknown */
export const getPullRequestStatusMessage = (status?: string) =>
	(status && pullRequestStatusToMessage[status]) ?? pullRequestStatusMessages.status_unknown;
